<script setup lang="ts">
import {
	ref,
	computed,
	onMounted,
	onBeforeMount,
	onBeforeUnmount,
	watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useApiClient } from "@/api/client";
import { useBaseStore } from "@/stores/base";
import { useCampaignPlannerStore } from "../../../store";
import MainBreadcrumbs from "@/ui/components/header/MainBreadcrumbs.vue";
import { availabilityColor } from "./availabilityColor";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import FlightSegmentSummaryDO = Modules.CampaignPlanner.Data.FlightSegmentSummaryDO;

const router = useRouter();
const isFullscreen = ref(true);

onBeforeMount(() => (useBaseStore().appFullWidth = true));

const store = useCampaignPlannerStore();
const flight_uuid: string = (useRoute().params.flight_uuid ?? "").toString();

const flight = ref<FlightDO | null>(null);
const segments = ref<FlightSegmentSummaryDO[]>([]);
const loading = ref(true);

const formatDate = (d: string) => dayjs(d).locale("nl").format("D MMM YYYY");

const formatShortDate = (d: string) => dayjs(d).locale("nl").format("D MMM");

const formatDateRange = (start: string, end: string) =>
	start === end
		? formatShortDate(start)
		: `${formatShortDate(start)} — ${formatShortDate(end)}`;

const segmentProgress = (seg: FlightSegmentSummaryDO) => {
	if (!seg.steps_count) return 0;
	return Math.round(((seg.steps_processed ?? 0) / seg.steps_count) * 100);
};

const segmentStatusLabel = (seg: FlightSegmentSummaryDO) => {
	const pct = segmentProgress(seg);
	if (seg.has_error_occurred) return "Failed";
	if (pct === 100) return "Complete";
	if (pct > 0) return "Processing";
	return "Queued";
};

const allCompleteWithoutErrors = computed(
	() => allComplete.value && segments.value.every((s) => !s.has_error_occurred)
);

const allComplete = computed(() => {
	if (segments.value.length === 0) return false;
	return segments.value.every(
		(s) =>
			s.has_error_occurred ||
			(s.steps_count != null &&
				s.steps_count > 0 &&
				(s.steps_processed ?? 0) >= s.steps_count)
	);
});

const countdownSec = ref(0);
let countdownTimer: ReturnType<typeof setInterval> | null = null;

const navigateToRefine = () => {
	if (!flight.value) return;
	if (countdownTimer !== null) {
		clearInterval(countdownTimer);
		countdownTimer = null;
	}
	router.push({
		name: "flight.detail.refine.segments",
		params: {
			flight_uuid: flight.value.uuid,
		},
	});
};

const startCountdown = (seconds = 10) => {
	if (countdownTimer !== null) return;
	countdownSec.value = seconds;
	countdownTimer = setInterval(() => {
		countdownSec.value--;
		if (countdownSec.value <= 0 && allCompleteWithoutErrors.value)
			navigateToRefine();
	}, 1000);
};

let pollTimer: ReturnType<typeof setInterval> | null = null;

const stopPolling = () => {
	if (pollTimer !== null) {
		clearInterval(pollTimer);
		pollTimer = null;
	}
};

const refreshSegments = async () => {
	const segRes = await useApiClient("campaignPlanner").getFlightSegments(
		flight_uuid
	);
	if (segRes.isOk()) segments.value = segRes.value;
	if (allComplete.value) stopPolling();
};

const startPolling = () => {
	if (pollTimer !== null || allComplete.value) return;
	pollTimer = setInterval(refreshSegments, 2000);
};

onBeforeUnmount(() => {
	useBaseStore().appFullWidth = false;
	stopPolling();
	if (countdownTimer !== null) {
		clearInterval(countdownTimer);
		countdownTimer = null;
	}
});

onMounted(async () => {
	const flightRes = await useApiClient("campaignPlanner").getFlight(
		flight_uuid
	);
	if (flightRes.isOk()) {
		flight.value = flightRes.value;
		const f = flightRes.value;

		const oppName = f.opportunity?.name ?? "Opportunity";

		store.setBreadcrumbs([
			{
				title: "Opportunities",
				disabled: false,
				to: "/campaign-planner/opportunities/listing",
			},
			{
				title: oppName,
				disabled: false,
				to: `/campaign-planner/opportunities/detail/${f.opportunity_uuid}`,
			},
			{ title: f.name ?? "Flight", disabled: true, to: "" },
		]);

		const segRes = await useApiClient("campaignPlanner").getFlightSegments(
			flight_uuid
		);
		if (segRes.isOk()) segments.value = segRes.value;
	}

	loading.value = false;
	startPolling();
});

const retryProcessing = async (flightSegment: FlightSegmentSummaryDO) => {
	await useApiClient("campaignPlanner").retryFlightSegment(
		flight_uuid,
		flightSegment.uuid
	);
	await refreshSegments();
	startPolling();
};

watch(allCompleteWithoutErrors, (v) => {
	if (v) startCountdown();
});
</script>

<template>
	<Teleport to="body" :disabled="!isFullscreen">
		<div :class="isFullscreen ? 'flight-fullscreen' : 'flight-inapp'">
			<div v-if="loading" class="d-flex align-center justify-center h-100">
				<v-progress-circular indeterminate color="primary" />
			</div>

			<template v-else-if="flight">
				<!-- Toolbar -->
				<v-toolbar v-if="isFullscreen" flat height="60">
					<div class="pl-5">
						<main-breadcrumbs :breadcrumbs="store.breadcrumbs" inline />
					</div>
					<v-spacer />
					<v-tooltip text="Collapse to in-app view" location="bottom">
						<template v-slot:activator="{ props }">
							<v-btn
								icon="fa: fa-regular fa-compress"
								v-bind="props"
								@click="isFullscreen = false"
							/>
						</template>
					</v-tooltip>
				</v-toolbar>
				<v-divider />

				<!-- Content -->
				<div class="flight-content">
					<div class="px-6 py-4 d-flex align-center">
						<v-avatar
							color="primary"
							variant="tonal"
							size="44"
							rounded="lg"
							class="mr-4"
						>
							<v-icon size="18"> fa: fa-light fa-billboard </v-icon>
						</v-avatar>
						<div class="flex-grow-1">
							<div class="text-body-1 font-weight-medium mb-1">
								{{ flight.name }}
							</div>
							<div class="text-body-2 text-medium-emphasis d-flex align-center">
								<span class="mr-3"
									>{{ formatDate(flight.start_date) }} —
									{{ formatDate(flight.end_date) }}</span
								>
								<v-chip size="x-small" color="primary" variant="tonal">
									{{ flight.is_digital ? "Digital" : "Static" }}
								</v-chip>
							</div>
						</div>
					</div>
					<v-divider />
					<div class="d-flex">
						<div class="stat-row-cell pa-4 text-center flex-1-1">
							<div class="text-caption text-medium-emphasis mb-1">Segments</div>
							<div class="text-h6 font-weight-bold">{{ segments.length }}</div>
						</div>
						<v-divider vertical />
						<div class="stat-row-cell pa-4 text-center flex-1-1">
							<div class="text-caption text-medium-emphasis mb-1">
								{{
									flight.goal_name_clean === "reach"
										? "Contacts"
										: "Impressions"
								}}
							</div>
							<div class="text-h6 font-weight-bold">
								{{ flight.goal_target.toLocaleString("nl-NL") }}
							</div>
						</div>
					</div>
					<v-divider />

					<!-- Segments grid -->

					<div class="pa-6">
						<div class="text-body-1 font-weight-medium mb-4">Segments:</div>
						<div
							v-if="segments.length === 0"
							class="d-flex flex-column align-center justify-center py-16 text-medium-emphasis"
						>
							<v-icon size="48" class="mb-4" opacity="0.3"
								>fa: fa-regular fa-calendar-xmark</v-icon
							>
							<div class="text-body-1">No segments found for this flight.</div>
						</div>

						<v-row v-else>
							<v-col
								v-for="seg in segments"
								:key="seg.uuid"
								cols="12"
								sm="6"
								lg="4"
								xl="3"
							>
								<v-card class="segment-card h-100">
									<v-card-item>
										<v-card-title class="text-body-1 font-weight-medium">
											{{ formatDateRange(seg.start_date, seg.end_date) }}
										</v-card-title>
										<template v-slot:append>
											<v-chip color="primary" size="x-small" variant="tonal">
												{{ segmentStatusLabel(seg) }}
											</v-chip>
										</template>
									</v-card-item>

									<v-divider />

									<v-card-text class="pa-3">
										<div class="progress-block pa-3">
											<div
												class="d-flex justify-space-between align-center mb-2"
											>
												<span class="text-caption text-medium-emphasis"
													>Progress:</span
												>
												<span
													class="text-caption font-weight-medium"
													:class="seg.has_error_occurred ? 'text-error' : ''"
												>
													{{ seg.steps_processed ?? 0 }} /
													{{ seg.steps_count ?? 0 }} steps
												</span>
											</div>
											<v-progress-linear
												v-if="!seg.has_error_occurred"
												:model-value="segmentProgress(seg)"
												:color="availabilityColor(segmentProgress(seg))"
												rounded
												height="6"
												:striped="seg.steps_processed !== seg.steps_count"
											/>
											<v-alert v-else color="error">
												<div class="d-flex">
													<div
														style="overflow: hidden; text-wrap: balance"
														class="pr-5"
													>
														<span class="text-caption font-weight-medium"
															>Unable to process flight-segment:<br />
															{{ seg.error_message }}</span
														>
													</div>
													<div class="d-flex align-end">
														<v-btn
															prepend-icon="fa: fa-light fa-arrow-rotate-right"
															size="small"
															color="error"
															variant="tonal"
															@click="retryProcessing(seg)"
															>Retry</v-btn
														>
													</div>
												</div>
											</v-alert>
										</div>
									</v-card-text>
								</v-card>
							</v-col>
						</v-row>
					</div>

					<div
						class="pa-6"
						v-if="allCompleteWithoutErrors && segments.length > 0"
					>
						<v-alert color="success" :icon="false">
							All flight-segments have been successfully processed.
						</v-alert>
						<div class="d-flex justify-end mt-4">
							<v-btn color="primary" variant="tonal" @click="navigateToRefine">
								Next in {{ countdownSec }} s.
							</v-btn>
						</div>
					</div>

					<v-tooltip
						v-if="!isFullscreen"
						text="Expand to full screen"
						location="bottom"
					>
						<template v-slot:activator="{ props }">
							<v-btn
								icon="fa: fa-regular fa-expand"
								variant="elevated"
								size="small"
								v-bind="props"
								class="expand-btn"
								@click="isFullscreen = true"
							/>
						</template>
					</v-tooltip>
				</div>
			</template>
		</div>
	</Teleport>
</template>

<style scoped lang="scss">
.flight-fullscreen {
	position: fixed;
	inset: 0;
	z-index: 1999;
	background: rgb(var(--v-theme-background));
	display: flex;
	flex-direction: column;
}

.flight-inapp {
	display: flex;
	flex-direction: column;
	height: calc(100dvh - var(--v-layout-top) - 56px);
	overflow: hidden;
}

.flight-content {
	display: flex;
	flex-direction: column;
	flex: 1;
	min-height: 0;
	position: relative;
}

.segments-scroll {
	flex: 1;
	min-height: 0;
}

.stat-block {
	min-width: 80px;
}

// V3: left primary accent line
.header-accent-border {
	border-left: 3px solid rgb(var(--v-theme-primary));
}

// V5: equal-width stat cells in the bottom bar
.stat-row-cell {
	flex: 1 1 0;
}

.segment-card {
	transition: box-shadow 0.2s ease;

	&:hover {
		box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08) !important;
	}
}

.progress-block {
	background: rgba(var(--v-theme-on-surface), 0.04);
	border-radius: 8px;
}

.expand-btn {
	position: absolute;
	bottom: 24px;
	right: 24px;
}
</style>
